import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { isHelpdeskMode } from '../../utils/helpdeskMode';
import {
  Box, Typography, TextField, Button, Paper,
  CircularProgress, Alert, InputAdornment, IconButton,
} from '@mui/material';
import {
  Visibility, VisibilityOff,
  SupportAgent as SupportIcon,
} from '@mui/icons-material';
import { usePortalAuthStore } from '../../stores/portalAuthStore';
import { useBranding } from '../../utils/BrandingContext';

const HelpdeskPortalLogin: React.FC = () => {
  const navigate = useNavigate();
  const basePath = isHelpdeskMode() ? '/support' : '/helpdesk';
  const { login, isAuthenticated, isLoading, error, clearError } = usePortalAuthStore();
  const { branding } = useBranding();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/portal/tickets', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    try {
      await login(window.location.hostname, email, password);
    } catch {
      // error is set in store
    }
  };

  const primaryColor = branding.primaryColor || '#6366f1';

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: '#f0f2f5',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        px: 2,
      }}
    >
      {/* Logo + Brand */}
      <Box sx={{ mb: 3, textAlign: 'center' }}>
        {branding.logo ? (
          <Box
            component="img"
            src={branding.logo}
            alt={branding.companyName || 'Support'}
            sx={{ height: 48, mb: 1.5, objectFit: 'contain' }}
            onError={(e: React.SyntheticEvent<HTMLImageElement>) => { e.currentTarget.style.display = 'none'; }}
          />
        ) : (
          <Box
            sx={{
              width: 48, height: 48, borderRadius: '12px',
              bgcolor: primaryColor, display: 'flex', alignItems: 'center',
              justifyContent: 'center', mx: 'auto', mb: 1.5,
            }}
          >
            <SupportIcon sx={{ color: '#fff', fontSize: 28 }} />
          </Box>
        )}
        <Typography variant="h6" fontWeight={700} color="#1f2937">
          {branding.companyName || 'Support Portal'}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Sign in to view and manage your support tickets
        </Typography>
      </Box>

      <Paper
        elevation={0}
        sx={{
          width: '100%', maxWidth: 400, p: 4,
          borderRadius: '16px', border: '1px solid #e5e7eb',
        }}
      >
        <Typography variant="h6" fontWeight={700} sx={{ mb: 3, color: '#1f2937' }}>
          Customer Login
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={clearError}>
            {error}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit}>
          <TextField
            label="Email address"
            type="email"
            fullWidth
            size="small"
            value={email}
            onChange={e => setEmail(e.target.value)}
            required
            autoComplete="email"
            sx={{ mb: 2 }}
          />

          <TextField
            label="Password"
            type={showPw ? 'text' : 'password'}
            fullWidth
            size="small"
            value={password}
            onChange={e => setPassword(e.target.value)}
            required
            autoComplete="current-password"
            sx={{ mb: 3 }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setShowPw(v => !v)} edge="end">
                    {showPw ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <Button
            type="submit"
            variant="contained"
            fullWidth
            disabled={isLoading || !email || !password}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: '8px',
              py: 1.1,
              bgcolor: primaryColor,
              '&:hover': { bgcolor: primaryColor, filter: 'brightness(0.9)' },
            }}
          >
            {isLoading ? <CircularProgress size={22} sx={{ color: '#fff' }} /> : 'Sign In'}
          </Button>
        </Box>

        <Box sx={{ mt: 2.5, textAlign: 'center' }}>
          <Typography variant="caption" color="text.secondary">
            Don't have an account?{' '}
            <Box
              component="span"
              sx={{ color: primaryColor, cursor: 'pointer', fontWeight: 600 }}
              onClick={() => navigate(basePath)}
            >
              Submit a ticket without logging in
            </Box>
          </Typography>
        </Box>
      </Paper>

      {/* Staff login link */}
      <Box sx={{ mt: 3, textAlign: 'center' }}>
        <Typography variant="caption" color="text.secondary">
          Are you a support agent?{' '}
          <Box
            component="span"
            sx={{ color: '#6b7280', cursor: 'pointer', textDecoration: 'underline' }}
            onClick={() => navigate('/login/direct')}
          >
            Staff login
          </Box>
        </Typography>
      </Box>
    </Box>
  );
};

export default HelpdeskPortalLogin;
